// Shared presentational pieces for the internal chat bubbles, used by both
// ChatPanel.tsx (entity "Interne" tab, dark mode) and MessagerieScreen.tsx
// (flat panel, light styling). Each component takes a `dark?: boolean` so it
// renders correctly in either host. Ported from hexagone's MessagerieThread.
import React, { useEffect, useRef, useState } from "react";
import Icon1 from "@assets/icons/Icon";
import { dossiersApi } from "@services/api/dossiers";
import { ordresApi } from "@services/api/ordres";
import { useDataStore } from "@store/user_org";
import { navigateTo } from "../../context/navigation.service";
import type { ChatAttachment, ChatMessage, PinnedMessage } from "../../types/messaging";

// ─── Attachment helpers ─────────────────────────────────────

export function isImageAtt(a?: ChatAttachment | null): boolean {
  return !!a && (a.type === "image" || (a.mime_type || a.mimeType || "").startsWith("image/"));
}

export function isAudioAtt(a?: ChatAttachment | null): boolean {
  return !!a && (a.mime_type || a.mimeType || "").startsWith("audio/");
}

export function isPdfAtt(a?: ChatAttachment | null): boolean {
  return (
    !!a &&
    ((a.name || "").toLowerCase().endsWith(".pdf") ||
      (a.mime_type || a.mimeType || "") === "application/pdf")
  );
}

/** Prefer the attachments[] list; fall back to the legacy single attachment. */
export function getAttachments(message: Pick<ChatMessage, "attachments" | "attachment">): ChatAttachment[] {
  if (message.attachments && message.attachments.length) return message.attachments;
  return message.attachment ? [message.attachment] : [];
}

export function formatSize(bytes?: number): string {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  const mo = bytes / (1024 * 1024);
  return `${mo < 10 ? mo.toFixed(1) : Math.round(mo)} Mo`;
}

function attSrc(a: ChatAttachment): string {
  return a.base64Data || a.url || "";
}

// ─── Image album (WhatsApp-style) ───────────────────────────
// 1 image full width; 2-4 in a 2-col grid (3 → first spans 2 cols);
// >4 shows a "+N" overlay on the 4th tile.

function ImageAlbumImpl({
  images,
  hovered,
  onOpen,
  onDownload,
  width = 240,
}: {
  images: ChatAttachment[];
  hovered?: boolean;
  onOpen: (a: ChatAttachment) => void;
  onDownload: (a: ChatAttachment) => void;
  width?: number;
}) {
  if (!images.length) return null;

  if (images.length === 1) {
    const a = images[0];
    return (
      <div
        onClick={(e) => { e.stopPropagation(); onOpen(a); }}
        title="Agrandir"
        style={{ width, position: "relative", cursor: "pointer", borderRadius: 8, overflow: "hidden" }}
      >
        <img src={attSrc(a)} alt={a.name || "image"} style={{ display: "block", width: "100%", height: "auto" }} />
        {hovered && (
          <div className="absolute flex flex-row" style={{ top: 6, left: 6, gap: 6 }}>
            <span className="flex items-center justify-center" style={{ width: 28, height: 28, borderRadius: 6, background: "rgba(0,0,0,0.55)" }}>
              <Icon1 name="eye" size={15} color="#FFFFFF" />
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); onDownload(a); }}
              title="Télécharger"
              className="flex items-center justify-center"
              style={{ width: 28, height: 28, borderRadius: 6, background: "rgba(0,0,0,0.55)", border: "none", cursor: "pointer" }}
            >
              <Icon1 name="download" size={15} color="#FFFFFF" />
            </button>
          </div>
        )}
      </div>
    );
  }

  const shown = images.slice(0, 4);
  const extra = images.length - 4;
  return (
    <div style={{ width, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, borderRadius: 8, overflow: "hidden" }}>
      {shown.map((a, i) => {
        const wideFirst = images.length === 3 && i === 0;
        return (
          <div
            key={i}
            onClick={(e) => { e.stopPropagation(); onOpen(a); }}
            style={{ position: "relative", cursor: "pointer", overflow: "hidden", gridColumn: wideFirst ? "1 / span 2" : undefined, aspectRatio: wideFirst ? "2 / 1" : "1 / 1" }}
          >
            <img src={attSrc(a)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            {i === 3 && extra > 0 && (
              <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.5)" }}>
                <span style={{ color: "#FFFFFF", fontFamily: "Poppins", fontWeight: 600, fontSize: 22 }}>+{extra}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Pure presentational album — memoized (shallow compare of primitives + small arrays).
export const ImageAlbum = React.memo(ImageAlbumImpl);

// ─── PDF card (WhatsApp-style) ──────────────────────────────
// page-1 thumbnail (preview_url) or a PDF glyph + name + meta + Ouvrir/Enregistrer.

function PdfCardImpl({
  att,
  isOwn,
  dark,
  onOpen,
  onDownload,
  width = 240,
}: {
  att: ChatAttachment;
  isOwn?: boolean;
  dark?: boolean;
  onOpen: (a: ChatAttachment) => void;
  onDownload: (a: ChatAttachment) => void;
  width?: number;
}) {
  // On a dark/own bubble the text sits on a tinted surface; otherwise a card.
  const onTinted = !!(dark || isOwn);
  const accent = onTinted ? "#FFFFFF" : "#6366f1";
  const divider = onTinted ? "rgba(255,255,255,0.16)" : "#F0F0F0";
  const cardBg = onTinted ? "rgba(255,255,255,0.08)" : "#FFFFFF";
  const cardBorder = onTinted ? "none" : "1px solid #ECECEC";
  const titleColor = onTinted ? "#FFFFFF" : "#000000";
  const metaColor = onTinted ? "rgba(255,255,255,0.6)" : "#A1A1A1";

  const meta = [
    att.pages ? `${att.pages} page${att.pages > 1 ? "s" : ""}` : null,
    "PDF",
    formatSize(att.size),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div style={{ width, borderRadius: 8, overflow: "hidden", background: cardBg, border: cardBorder }}>
      {att.preview_url && (
        <div onClick={(e) => { e.stopPropagation(); onOpen(att); }} style={{ cursor: "pointer", background: "#E9E9E9" }}>
          <img src={att.preview_url} alt="" style={{ display: "block", width: "100%", height: 130, objectFit: "cover", objectPosition: "top" }} />
        </div>
      )}
      <div className="flex flex-row items-center" style={{ gap: 8, padding: "8px 10px" }}>
        <div className="flex items-center justify-center" style={{ width: 28, height: 32, borderRadius: 5, background: "#FF3B30", flexShrink: 0 }}>
          <span style={{ color: "#FFFFFF", fontFamily: "Poppins", fontWeight: 700, fontSize: 8 }}>PDF</span>
        </div>
        <div className="flex flex-col" style={{ flex: 1, overflow: "hidden" }}>
          <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: titleColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{att.name || "Document.pdf"}</span>
          <span style={{ fontFamily: "Poppins", fontSize: 11, color: metaColor }}>{meta}</span>
        </div>
      </div>
      <div className="flex flex-row" style={{ borderTop: `1px solid ${divider}` }}>
        <button onClick={(e) => { e.stopPropagation(); onOpen(att); }} className="flex-1 hover:opacity-80" style={{ padding: 8, background: "transparent", border: "none", cursor: "pointer", fontFamily: "Poppins", fontWeight: 600, fontSize: 12, color: accent }}>Ouvrir</button>
        <div style={{ width: 1, background: divider }} />
        <button onClick={(e) => { e.stopPropagation(); onDownload(att); }} className="flex-1 hover:opacity-80" style={{ padding: 8, background: "transparent", border: "none", cursor: "pointer", fontFamily: "Poppins", fontWeight: 600, fontSize: 12, color: accent }}>Enregistrer</button>
      </div>
    </div>
  );
}

// Pure presentational PDF card — memoized (shallow compare is sufficient).
export const PdfCard = React.memo(PdfCardImpl);

// ─── Day separators ─────────────────────────────────────────

/** Calendar-day bucket for a message (local time). Used both to break message
 * groups across midnight and to render day separators. */
export function msgDayKey(msg?: Pick<ChatMessage, "created_at" | "timestamp"> | null): string {
  const iso = msg && (msg.created_at || msg.timestamp);
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toDateString();
}

/** "Aujourd'hui" / "Hier" / "DD/MM/YYYY" */
export function formatDaySeparator(iso?: string): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return "Aujourd'hui";
    const yest = new Date(now);
    yest.setDate(now.getDate() - 1);
    if (d.toDateString() === yest.toDateString()) return "Hier";
    return d.toLocaleDateString("fr-FR");
  } catch {
    return "";
  }
}

function DaySeparatorImpl({ iso, dark }: { iso?: string; dark?: boolean }) {
  const label = formatDaySeparator(iso);
  if (!label) return null;
  return (
    <div className="flex items-center justify-center" style={{ margin: "2px 0" }}>
      <span
        style={{
          fontFamily: "Poppins",
          fontWeight: 500,
          fontSize: 12,
          color: dark ? "#A1A1A1" : "#525252",
          background: dark ? "#2D2D2D" : "#FFFFFF",
          border: `1px solid ${dark ? "#404040" : "#ECECEC"}`,
          borderRadius: 999,
          padding: "4px 12px",
          boxShadow: dark ? "none" : "0 1px 2px rgba(0,0,0,0.04)",
        }}
      >
        {label}
      </span>
    </div>
  );
}

// Pure presentational day separator — memoized.
export const DaySeparator = React.memo(DaySeparatorImpl);

// ─── Linkify (phone / URL / email) ──────────────────────────
// Group 1: URL, 2: email, 3: phone (FR formats like "06 12 34 56 78",
// "+33 6 12 34 56 78", "0612345678", plus generic intl).
export const LINKIFY_RE = new RegExp(
  [
    "(https?:\\/\\/[^\\s]+)",
    "([\\w.+-]+@[\\w-]+\\.[\\w.-]+)",
    "((?:(?:\\+|00)33[\\s.\\-]?(?:\\(0\\)[\\s.\\-]?)?|0)[1-9](?:[\\s.\\-]?\\d{2}){4}" +
      "|(?:\\+|00)\\d{1,3}[\\s.\\-]?\\d(?:[\\s.\\-]?\\d){7,12})",
  ].join("|"),
  "g"
);

/** Turn a plain text run into nodes, replacing phone/URL/email matches with
 * clickable links (open / mail / call). Returns the raw string untouched when
 * there's nothing to linkify. Links stopPropagation so they don't trigger the
 * bubble click handlers, and stay selectable. */
export function linkify(text: string, linkColor: string): React.ReactNode {
  if (!text) return text;
  const re = new RegExp(LINKIFY_RE); // fresh instance: no shared lastIndex
  let m = re.exec(text);
  if (!m) return text;
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let key = 0;
  while (m !== null) {
    const [match, url, email, phone] = m;
    if (m.index > last) nodes.push(text.slice(last, m.index));
    let href = "";
    let external = false;
    if (url) { href = url; external = true; }
    else if (email) href = `mailto:${email}`;
    else if (phone) href = `tel:${phone.replace(/[^\d+]/g, "")}`;
    nodes.push(
      <a
        key={`lk${key++}`}
        href={href}
        onClick={(e) => e.stopPropagation()}
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        style={{ color: linkColor, textDecoration: "underline" }}
      >
        {match}
      </a>
    );
    last = m.index + match.length;
    m = re.exec(text);
  }
  if (last < text.length) nodes.push(text.slice(last));
  return <>{nodes}</>;
}

// ─── Pin glyph ──────────────────────────────────────────────
// Small pin shown next to the timestamp on a pinned bubble.

function PinGlyphImpl({ dark, isOwn, size = 11 }: { dark?: boolean; isOwn?: boolean; size?: number }) {
  const color = dark || isOwn ? "rgba(255,255,255,0.7)" : "#737373";
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }} aria-hidden>
      <path
        d="M9 4h6l-1 6 3 3v2H7v-2l3-3-1-6zM12 15v5"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Pure presentational pin glyph — memoized.
export const PinGlyph = React.memo(PinGlyphImpl);

// ─── Pinned bar ─────────────────────────────────────────────
// Banner at the top of the thread listing pinned messages. A single pin shows
// the sender + preview; multiple pins collapse to "N messages épinglés" that
// expands to a scrollable list. Each row jumps to the message; each has an
// unpin ✕. Ported from hexagone's PinnedBar.

function pinnedPreview(p: PinnedMessage): string {
  if (p.text && p.text.trim()) return p.text;
  if (p.attachment_type === "image") return "📷 Photo";
  if (p.attachment_type) return "📎 Pièce jointe";
  return "Message";
}

function PinnedBarImpl({
  pins,
  dark,
  onJump,
  onUnpin,
}: {
  pins: PinnedMessage[];
  dark?: boolean;
  onJump: (messageId: string) => void;
  onUnpin: (messageId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  if (!pins || pins.length === 0) return null;

  const bg = dark ? "#2D2D3D" : "#FFFFFF";
  const border = dark ? "#333348" : "#E5E5E5";
  const accent = dark ? "#A5B4FC" : "#6366f1";
  const titleColor = dark ? "#E5E5E5" : "#1E1E1E";
  const subColor = dark ? "#9CA3AF" : "#6B7280";
  const rowHover = dark ? "rgba(255,255,255,0.06)" : "#F5F5F5";

  const Row = ({ p }: { p: PinnedMessage }) => (
    <div
      className="flex flex-row items-center"
      style={{ gap: 8, padding: "6px 12px", cursor: "pointer" }}
      onClick={() => onJump(p.message_id)}
      onMouseEnter={(e) => (e.currentTarget.style.background = rowHover)}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <PinGlyph dark={dark} size={13} />
      <div className="flex flex-col" style={{ flex: 1, overflow: "hidden" }}>
        <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 12, color: accent, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {p.sender_name}
        </span>
        <span style={{ fontFamily: "Poppins", fontSize: 12, color: subColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {pinnedPreview(p)}
        </span>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onUnpin(p.message_id); }}
        className="flex items-center justify-center flex-shrink-0"
        style={{ width: 22, height: 22, borderRadius: 6 }}
        title="Détacher"
      >
        <Icon1 name="close" size={11} color={subColor} />
      </button>
    </div>
  );

  return (
    <div style={{ background: bg, borderBottom: `1px solid ${border}`, flexShrink: 0 }}>
      {pins.length === 1 ? (
        <Row p={pins[0]} />
      ) : (
        <>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="flex flex-row items-center w-full"
            style={{ gap: 8, padding: "8px 12px", background: "transparent", border: "none", cursor: "pointer" }}
          >
            <PinGlyph dark={dark} size={13} />
            <span style={{ flex: 1, textAlign: "left", fontFamily: "Poppins", fontWeight: 600, fontSize: 12, color: titleColor }}>
              {pins.length} messages épinglés
            </span>
            <svg width={10} height={10} viewBox="0 0 10 10" fill="none">
              <path d={expanded ? "M8 7L5 4L2 7" : "M2 3L5 6L8 3"} stroke={subColor} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {expanded && (
            <div style={{ maxHeight: 200, overflowY: "auto", borderTop: `1px solid ${border}` }}>
              {pins.map((p) => <Row key={p.message_id} p={p} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Memoized — default shallow compare. The host must pass stable `onJump`/`onUnpin`
// (useCallback) so this only re-renders when `pins`/`dark` actually change.
export const PinnedBar = React.memo(PinnedBarImpl);

// ─── Folder tags (/Name chips) ──────────────────────────────
// A tracked folder reference picked in the composer via the "/" trigger.
export interface FolderRef {
  id: string;
  name: string;
  kind: "dossier" | "ordre";
}

/** Navigate to a dossier / ordre detail screen, matching heric's routes. */
export function navigateToFolderRef(ref: { id: string; name?: string; kind?: "dossier" | "ordre" }): void {
  // folder_refs persisted on messages may not carry `kind`; default to dossier.
  const route = ref.kind === "ordre" ? `/ordre/${ref.id}` : `/dossier/${ref.id}`;
  navigateTo({ route });
}

/** Render message text, turning "/Name" tokens that match a known folder_ref
 * into clickable chips that navigate to that dossier/ordre. Other text is
 * linkified normally. Falls back to plain linkify when there are no refs. */
export function renderTextWithFolderTokens(
  text: string,
  folderRefs: { id: string; name: string }[] | undefined,
  linkColor: string,
  dark?: boolean,
  isOwn?: boolean,
): React.ReactNode {
  if (!folderRefs || folderRefs.length === 0) return linkify(text, linkColor);
  // Longest names first so "/Foo Bar" wins over "/Foo".
  const refs = [...folderRefs].sort((a, b) => b.name.length - a.name.length);
  const escaped = refs.map((r) => r.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`/(${escaped.join("|")})`, "g");
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(<React.Fragment key={`t${key++}`}>{linkify(text.slice(last, m.index), linkColor)}</React.Fragment>);
    const ref = refs.find((r) => r.name === m![1]);
    if (ref) {
      nodes.push(
        <FolderChip key={`f${key++}`} name={ref.name} onClick={() => navigateToFolderRef({ id: ref.id, name: ref.name })} dark={dark} isOwn={isOwn} />
      );
    } else {
      nodes.push(m[0]);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(<React.Fragment key={`t${key++}`}>{linkify(text.slice(last), linkColor)}</React.Fragment>);
  return <>{nodes}</>;
}

function FolderChipImpl({ name, onClick, dark, isOwn }: { name: string; onClick: () => void; dark?: boolean; isOwn?: boolean }) {
  const onTinted = !!(dark || isOwn);
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className="inline-flex items-center"
      style={{
        gap: 3,
        padding: "1px 6px",
        margin: "0 1px",
        borderRadius: 5,
        verticalAlign: "baseline",
        background: onTinted ? "rgba(255,255,255,0.18)" : "#EEF2FF",
        color: onTinted ? "#FFFFFF" : "#4338CA",
        fontFamily: "Poppins",
        fontWeight: 600,
        fontSize: 12.5,
        border: "none",
        cursor: "pointer",
      }}
      title={`Ouvrir ${name}`}
    >
      <Icon1 name="folder" size={11} color={onTinted ? "#FFFFFF" : "#4338CA"} />
      {name}
    </button>
  );
}

// Pure presentational folder chip — memoized.
export const FolderChip = React.memo(FolderChipImpl);

// ─── Folder search (composer "/" trigger) ───────────────────
// Searches heric dossiers + ordres via their real list APIs (org-scoped),
// filtered client-side by the query. Debounced; no Algolia.

export function useFolderSearch(query: string | null): { results: FolderRef[]; loading: boolean } {
  const [results, setResults] = useState<FolderRef[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query === null) { setResults([]); setLoading(false); return; }
    const orgId = useDataStore.getState().userData?.org_id;
    if (!orgId) { setResults([]); setLoading(false); return; }
    const q = query.trim().toLowerCase();
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      Promise.all([
        dossiersApi.list({ org_id: orgId, limit: 50 }).catch(() => ({ items: [] as any[], total: 0 })),
        ordresApi.list({ org_id: orgId, limit: 50 }).catch(() => ({ items: [] as any[], total: 0 })),
      ])
        .then(([dossiers, ordres]) => {
          const dossierRefs: FolderRef[] = (dossiers.items || [])
            .map((d: any) => ({ id: d.id, name: (d.name || "").trim(), kind: "dossier" as const }))
            .filter((r) => r.name);
          const ordreRefs: FolderRef[] = (ordres.items || [])
            .map((o: any) => ({ id: o.id, name: (o.num || o.name || "").trim(), kind: "ordre" as const }))
            .filter((r) => r.name);
          const all = [...dossierRefs, ...ordreRefs];
          const filtered = q ? all.filter((r) => r.name.toLowerCase().includes(q)) : all;
          setResults(filtered.slice(0, 8));
        })
        .finally(() => setLoading(false));
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  return { results, loading };
}

export function FolderSearchPopover({
  results,
  loading,
  activeIndex,
  onPick,
  onHover,
  dark,
}: {
  results: FolderRef[];
  loading: boolean;
  activeIndex: number;
  onPick: (ref: FolderRef) => void;
  onHover: (i: number) => void;
  dark?: boolean;
}) {
  if (!loading && results.length === 0) return null;
  const bg = dark ? "#2D2D2D" : "#FFFFFF";
  const border = dark ? "#404040" : "#E5E5E5";
  const activeBg = dark ? "rgba(255,255,255,0.08)" : "#F5F5F5";
  const textColor = dark ? "#E5E5E5" : "#1E1E1E";
  const subColor = dark ? "#9CA3AF" : "#A1A1A1";
  return (
    <div
      className="flex flex-col"
      style={{
        marginBottom: 4,
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 10,
        boxShadow: dark ? "0px 4px 12px rgba(0,0,0,0.4)" : "0px 4px 12px rgba(0,0,0,0.12)",
        overflow: "hidden",
        maxHeight: 220,
        overflowY: "auto",
      }}
    >
      {loading && results.length === 0 ? (
        <div style={{ padding: "10px 12px", fontFamily: "Poppins", fontSize: 13, color: subColor }}>Recherche…</div>
      ) : (
        results.map((r, i) => (
          <button
            key={`${r.kind}-${r.id}`}
            className="flex flex-row items-center w-full text-left"
            style={{ padding: "8px 12px", gap: 10, background: i === activeIndex ? activeBg : "transparent" }}
            onMouseDown={(e) => { e.preventDefault(); onPick(r); }}
            onMouseEnter={() => onHover(i)}
          >
            <Icon1 name="folder" size={14} color={dark ? "#A5B4FC" : "#6366f1"} />
            <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: textColor, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {r.name}
            </span>
            <span style={{ fontFamily: "Poppins", fontSize: 11, color: subColor }}>
              {r.kind === "ordre" ? "Ordre" : "Dossier"}
            </span>
          </button>
        ))
      )}
    </div>
  );
}
